/**
 * Signature Section
 * Complete signature flow with filter tabs, signature cards, email modal, success dialog, and lenders submission
 */

import * as React from "react";
import { AlertCircleIcon, Agreement03Icon, toast } from "@porchpass/ui";
import {
  ChevronDownIcon,
  Cancel01Icon,
  Calendar01Icon,
  CheckListIcon,
  SignatureIcon,
  MailReply02Icon,
  CheckmarkCircle02Icon,
} from "@porchpass/ui";
import { Button } from "@porchpass/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@porchpass/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@porchpass/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipArrow,
} from "@porchpass/ui";
import { useNavigate } from "react-router-dom";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import { useDgaApi } from "../hooks/useDgaApi";
import { useDealer } from "src/hooks/useDealer";
import { useDgaModal } from "src/context/DgaModalContext";
import {
  isSigningCompletionPending,
  type SignatureStatus,
} from "../types/applicationTypes";
import {
  getSigningMode,
  isDocusignSigningComplete,
  type SigningMode,
} from "../utils/signingMode";
import { EditApplicationDialog } from "./EditApplicationDialog";
import {
  DomainApplicationStatus,
  DomainAppMilestoneType,
  DomainBorrowerStatus,
} from "@porchpass/api-client";
import type {
  DtoBorrowerProgress,
  DtoCoBorrowerProgress,
} from "@porchpass/api-client";
import {
  computeValidationFlags,
  mapBorrowerProgressToFieldErrors,
  mergeValidationErrors,
  preserveApiFieldErrors,
  validateApplication,
} from "../validation/validateApplication";

// ============================================================================
// Types
// ============================================================================

type FilterTab = "all" | "ready" | "not-ready" | "signed";

interface BorrowerSignatureInfo {
  id: "primary" | "co";
  name: string;
  role: string;
  email: string;
  status: SignatureStatus;
  initials: string;
  avatarColor: string;
}

interface BorrowerSignatureBase {
  id: "primary" | "co";
  name: string;
  role: string;
  email: string;
  initials: string;
  avatarColor: string;
  backendStatus: SignatureStatus;
}

interface BorrowerValidationFlags {
  primaryPassed: boolean;
  coPassed: boolean;
}

const JOINT_GATE_EXEMPT_STATUSES = new Set<SignatureStatus>([
  "signed",
  "validating",
  "preparing",
  "declined",
]);

function getRawEffectiveStatus(
  borrowerId: "primary" | "co",
  backendStatus: SignatureStatus,
  validation: BorrowerValidationFlags,
): SignatureStatus {
  const passed =
    borrowerId === "primary" ? validation.primaryPassed : validation.coPassed;
  if (!passed) {
    return "not-ready";
  }
  return backendStatus;
}

function applyJointSigningGate(
  rawStatus: SignatureStatus,
  allUnsignedBorrowersReady: boolean,
): SignatureStatus {
  if (JOINT_GATE_EXEMPT_STATUSES.has(rawStatus)) {
    return rawStatus;
  }
  if (!allUnsignedBorrowersReady) {
    return "not-ready";
  }
  if (rawStatus === "ready") {
    return "ready";
  }
  return "not-ready";
}

function matchesFilterTab(
  status: SignatureStatus,
  filterTab: FilterTab,
): boolean {
  if (filterTab === "all") {
    return true;
  }
  return status === filterTab;
}

function buildSignatureBorrowerView(input: {
  primary: BorrowerSignatureBase | null;
  co: BorrowerSignatureBase | null;
  validation: BorrowerValidationFlags;
  filterTab: FilterTab;
}): {
  borrowers: BorrowerSignatureInfo[];
  filteredBorrowers: BorrowerSignatureInfo[];
  counts: { all: number; ready: number; notReady: number; signed: number };
  allSigned: boolean;
} {
  const bases = [input.primary, input.co].filter(
    (b): b is BorrowerSignatureBase => b !== null,
  );

  const rawStatuses = bases.map((base) =>
    getRawEffectiveStatus(base.id, base.backendStatus, input.validation),
  );

  const allUnsignedBorrowersReady = bases.every((_, i) => {
    const raw = rawStatuses[i];
    return raw === "ready" || raw === "signed";
  });

  const anyValidating = rawStatuses.some((raw) => raw === "validating");

  const borrowers: BorrowerSignatureInfo[] = bases.map((base, i) => {
    let status = applyJointSigningGate(
      rawStatuses[i],
      allUnsignedBorrowersReady,
    );
    // While any borrower is awaiting backend signature confirmation, show
    // validating on all unsigned borrowers so the UI doesn't mislabel the
    // other borrower as "not ready").
    if (anyValidating && rawStatuses[i] !== "signed") {
      status = "validating";
    }
    return {
      id: base.id,
      name: base.name,
      role: base.role,
      email: base.email,
      initials: base.initials,
      avatarColor: base.avatarColor,
      status,
    };
  });

  const counts = { all: 0, ready: 0, notReady: 0, signed: 0 };
  const filteredBorrowers: BorrowerSignatureInfo[] = [];

  for (const borrower of borrowers) {
    counts.all++;
    if (borrower.status === "ready") {
      counts.ready++;
    } else if (borrower.status === "not-ready") {
      counts.notReady++;
    } else if (borrower.status === "signed") {
      counts.signed++;
    }

    if (matchesFilterTab(borrower.status, input.filterTab)) {
      filteredBorrowers.push(borrower);
    }
  }

  const allSigned =
    borrowers.length > 0 && borrowers.every((b) => b.status === "signed");

  return { borrowers, filteredBorrowers, counts, allSigned };
}

// ============================================================================
// Filter Tabs Component
// ============================================================================

interface FilterTabsProps {
  activeTab: FilterTab;
  onTabChange: (tab: FilterTab) => void;
  counts: { all: number; ready: number; signed: number; notReady: number };
}

const FilterTabs: React.FC<FilterTabsProps> = ({
  activeTab,
  onTabChange,
  counts,
}) => {
  const getCountBadgeStyle = (tabId: FilterTab) => {
    if (tabId === "ready" || tabId === "signed") {
      return "bg-[#dbf8e7] text-[#16a34a]";
    }
    if (tabId === "not-ready") {
      return "bg-[#f6eff1] text-[#df1b41]";
    }
    // "all" tab
    return "bg-[#e5ebfd] text-[#1453f5]";
  };

  const tabs: { id: FilterTab; label: string; count: number }[] = [
    { id: "all", label: "All", count: counts.all },
    { id: "ready", label: "Ready", count: counts.ready },
    { id: "not-ready", label: "Not ready", count: counts.notReady },
    { id: "signed", label: "Signed", count: counts.signed },
  ];

  return (
    <div className="flex gap-1 items-center">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 text-base font-medium transition-colors rounded-[999px]
            ${
              activeTab === tab.id
                ? "bg-[#e5ebfd] text-[#1453f5]"
                : "text-[#4a5466] hover:bg-[#f3f5f8]"
            }
          `}
        >
          {tab.label}
          <span
            className={`px-1.5 py-0.5 text-xs font-medium rounded-[999px] ${getCountBadgeStyle(tab.id)}`}
          >
            {tab.count}
          </span>
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// Avatar Component
// ============================================================================

interface AvatarProps {
  initials: string;
  color: string;
}

const Avatar: React.FC<AvatarProps> = ({ initials, color }) => (
  <div
    className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 uppercase text-[17.5px] font-semibold"
    style={{
      backgroundColor: color === "purple" ? "#f1edfb" : "#e5ebfd",
      color: color === "purple" ? "#6f41e6" : "#1453f5",
    }}
  >
    {initials}
  </div>
);

// ============================================================================
// Status Badge Components
// ============================================================================

const ReadyToSignBadge: React.FC = () => (
  <div className="flex gap-1 items-center">
    <CheckmarkCircle02Icon
      variant="solid-standard"
      size={12}
      className="text-[#22c55e]"
    />
    <span className="text-3xs font-medium text-[#111827]">Ready to sign</span>
  </div>
);

const ValidatingBadge: React.FC = () => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e5ebfd]">
    <div className="w-4 h-4 border-2 border-[#1453f5] border-t-transparent rounded-full animate-spin" />
    <span className="text-base font-medium text-[#1453f5]">
      Validating signature
    </span>
  </div>
);

const PreparingBadge: React.FC = () => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e5ebfd]">
    <div className="w-4 h-4 border-2 border-[#1453f5] border-t-transparent rounded-full animate-spin" />
    <span className="text-base font-medium text-[#1453f5]">
      Preparing for signature...
    </span>
  </div>
);

const AwaitingSignatureBadge: React.FC = () => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e5ebfd]">
    <div className="w-4 h-4 border-2 border-[#1453f5] border-t-transparent rounded-full animate-spin" />
    <span className="text-base font-medium text-[#1453f5]">
      Awaiting Signature...
    </span>
  </div>
);

const SignedBadge: React.FC = () => (
  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#dcfce7]">
    <SignatureIcon size={14} className="text-[#16a34a]" />
    <span className="text-base font-medium text-[#16a34a]">Signed</span>
  </div>
);

const NotReadyBadge: React.FC = () => (
  <div className="flex gap-1 items-center">
    <div className="w-2 h-2 rounded-full bg-[#d1d5db]" />
    <span className="text-3xs font-medium text-[#6b7280]">Not ready</span>
  </div>
);

// ============================================================================
// Sign Dropdown Button
// ============================================================================

interface SignDropdownProps {
  onSignHere: () => void;
  onSignViaEmail: () => void;
}

const SignDropdown: React.FC<SignDropdownProps> = ({
  onSignHere,
  onSignViaEmail,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="primary" size="s" className="gap-1">
        <Agreement03Icon variant="bulk-rounded" size={18} />
        Sign
        <ChevronDownIcon size={16} />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-[180px]">
      <DropdownMenuItem onClick={onSignHere} className="gap-4">
        <SignatureIcon size={18} />
        Sign here
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onSignViaEmail} className="gap-4">
        <MailReply02Icon size={18} />
        Sign via email
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

interface SignHereButtonProps {
  onSignHere: () => void;
}

const SignHereButton: React.FC<SignHereButtonProps> = ({ onSignHere }) => (
  <Button variant="primary" size="s" className="gap-1" onClick={onSignHere}>
    <SignatureIcon size={18} />
    Sign Here
  </Button>
);

// ============================================================================
// Signature Card Component
// ============================================================================

interface SignatureCardProps {
  borrower: BorrowerSignatureInfo;
  onSignHere: () => void;
  onSignViaEmail: () => void;
  showTooltip?: boolean;
  onTooltipClose?: () => void;
  isAwaitingSignature?: boolean;
  signingMode: SigningMode;
}

const SignatureCard: React.FC<SignatureCardProps> = ({
  borrower,
  onSignHere,
  onSignViaEmail,
  showTooltip,
  onTooltipClose,
  isAwaitingSignature = false,
  signingMode,
}) => {
  const renderStatus = () => {
    if (isAwaitingSignature) {
      return <AwaitingSignatureBadge />;
    }

    switch (borrower.status) {
      case "ready":
        return (
          <div className="flex gap-4 items-center">
            <TooltipProvider delayDuration={0}>
              <Tooltip open={showTooltip}>
                <TooltipTrigger asChild>
                  <div>
                    <ReadyToSignBadge />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[269px] p-4">
                  <button
                    onClick={onTooltipClose}
                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10"
                  >
                    <Cancel01Icon size={16} className="text-white/60" />
                  </button>
                  <div className="flex gap-2 items-center mb-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#dbf8e7]">
                      <CheckmarkCircle02Icon
                        variant="solid-standard"
                        size={16}
                        className="text-[#16a34a]"
                      />
                    </div>
                    <span className="text-base font-semibold text-white">
                      Ready to sign
                    </span>
                  </div>
                  <p className="text-xs font-medium text-white">
                    While ready for signature, we recommend waiting until the
                    application is complete to avoid having to re-sign.
                  </p>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-3xs text-[#6b7280]">3 mins ago</span>
          </div>
        );
      case "preparing":
        return <PreparingBadge />;
      case "validating":
        return <ValidatingBadge />;
      case "signed":
        return <SignedBadge />;
      case "not-ready":
      default:
        return <NotReadyBadge />;
    }
  };

  const renderAction = () => {
    if (isAwaitingSignature || signingMode === "email") {
      return null;
    }
    if (borrower.status === "ready") {
      if (signingMode === "embedded") {
        return <SignHereButton onSignHere={onSignHere} />;
      }
      return (
        <SignDropdown onSignHere={onSignHere} onSignViaEmail={onSignViaEmail} />
      );
    }
    return null;
  };

  // Ready state - nested card design with status below
  if (borrower.status === "ready" && !isAwaitingSignature) {
    return (
      <div className="bg-[#f3f5f8] rounded-[22px] p-1.5">
        <div className="bg-white rounded-2xl h-[72px] flex items-center justify-between px-4">
          <div className="flex gap-4 items-center">
            <Avatar
              initials={borrower.initials}
              color={borrower.id === "primary" ? "purple" : "blue"}
            />
            <div>
              <p className="text-[18px] font-medium text-[#111827]">
                {borrower.name}
              </p>
              <p className="text-xs text-[#4a5466]">{borrower.role}</p>
            </div>
          </div>
          {renderAction()}
        </div>
        <div className="flex gap-1 justify-center items-center py-2">
          {renderStatus()}
        </div>
      </div>
    );
  }

  // Validating/Signed/Not-ready - flat card with status on RIGHT
  return (
    <div className="bg-[#f3f5f8] rounded-2xl h-[72px] flex items-center justify-between px-4">
      <div className="flex gap-4 items-center">
        <Avatar
          initials={borrower.initials}
          color={borrower.id === "primary" ? "purple" : "blue"}
        />
        <div>
          <p className="text-[18px] font-medium text-[#111827]">
            {borrower.name}
          </p>
          <p className="text-xs text-[#4a5466]">{borrower.role}</p>
        </div>
      </div>
      {renderStatus()}
    </div>
  );
};

// ============================================================================
// Sign Via Email Dialog
// ============================================================================

interface EmailRecipient {
  name: string;
  role: string;
  email: string;
}

interface SignViaEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: () => void;
  recipients: EmailRecipient[];
}

const SignViaEmailDialog: React.FC<SignViaEmailDialogProps> = ({
  open,
  onOpenChange,
  onSend,
  recipients,
}) => {
  const hasMissingEmail = recipients.some((r) => !r.email.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[410px]">
        <DialogHeader>
          <DialogTitle className="flex gap-2 items-center text-md">
            <MailReply02Icon size={18} className="text-[#1453f5]" />
            Sign Via Email
          </DialogTitle>
        </DialogHeader>
        <div className="p-6 pt-4">
          <p className="text-base text-[#4a5466] mb-4">
            DocuSign signing instructions will be sent to the following
            addresses:
          </p>
          <ul className="space-y-4 mb-4">
            {recipients.map((recipient) => (
              <li key={recipient.role} className="text-base">
                <p className="text-xs text-[#4a5466]">{recipient.role}</p>
                <p className="font-medium text-[#111827]">{recipient.name}</p>
                <p className="text-[#111827]">
                  {recipient.email.trim() || (
                    <span className="text-[#df1b41]">No email on file</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
          {hasMissingEmail && (
            <p className="text-xs text-[#df1b41] mb-4">
              Add an email address for each borrower before sending signing
              instructions.
            </p>
          )}
          <div className="flex gap-3 mt-6">
            <Button
              variant="secondary"
              size="s"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="s"
              className="flex-1"
              onClick={onSend}
              disabled={hasMissingEmail}
            >
              Send instructions
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Success Dialog
// ============================================================================

interface SuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceedToLenders: () => void;
}

const SuccessDialog: React.FC<SuccessDialogProps> = ({
  open,
  onOpenChange,
  onProceedToLenders,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="sm:max-w-[846px] p-0 overflow-hidden"
      hideCloseButton
    >
      <div className="flex">
        {/* Left side - content */}
        <div className="flex-1 p-10">
          {/* Logo */}
          <svg
            width="27"
            height="23"
            viewBox="0 0 27 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mb-6"
          >
            <path d="M13.5 0L27 23H0L13.5 0Z" fill="#1453f5" />
          </svg>

          <h2 className="text-2xl font-medium text-[#111827] mb-8">
            Well done! 🎉 The application is now signed.
          </h2>

          <div className="mb-8">
            <p className="text-xs text-[#4a5466] mb-4">What{"'"}s next:</p>
            <div className="space-y-3">
              <div className="flex gap-4 items-center">
                <div className="flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-[#e5ebfd]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#1453f5]"
                  >
                    <path
                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 2V8H20"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-base text-[#111827]">
                  Submit the application for lender approval
                </span>
              </div>
              <div className="flex gap-4 items-center">
                <div className="flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-[#fef3c7]">
                  <Calendar01Icon size={16} className="text-[#d97706]" />
                </div>
                <span className="text-base text-[#111827]">
                  Set target delivery and funding dates
                </span>
              </div>
              <div className="flex gap-4 items-center">
                <div className="flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-[#fce7f3]">
                  <CheckListIcon size={16} className="text-[#db2777]" />
                </div>
                <span className="text-base text-[#111827]">
                  Upload conditions and track tasks
                </span>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#4a5466] mb-6">
            View progress and next steps on the application details page.
          </p>

          <Button
            variant="primary"
            size="m"
            className="w-full"
            onClick={onProceedToLenders}
          >
            Submit to Lenders
          </Button>
        </div>

        {/* Right side - illustration */}
        <div className="w-[270px] bg-linear-to-br from-[#e8f4f8] to-[#d1e7dd] relative overflow-hidden">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-2 right-2 z-10 p-2 rounded-full hover:bg-white/20"
          >
            <Cancel01Icon size={16} className="text-[#6b7280]" />
          </button>
          {/* Confetti decoration */}
          <div className="flex absolute inset-0 justify-center items-center">
            <div className="relative">
              {/* Document illustration */}
              <div className="w-[100px] h-[130px] bg-white rounded-lg shadow-lg flex flex-col p-4 relative">
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#22c55e] rounded-full flex items-center justify-center">
                  <CheckmarkCircle02Icon
                    variant="solid-standard"
                    size={20}
                    className="text-white"
                  />
                </div>
                <div className="w-12 h-1.5 bg-[#e5e7eb] rounded mb-2" />
                <div className="w-8 h-1.5 bg-[#e5e7eb] rounded mb-2" />
                <div className="w-10 h-1.5 bg-[#e5e7eb] rounded mb-4" />
                <div className="mt-auto">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#1453f5]"
                  >
                    <path
                      d="M4 18V14M20 18V14M12 18V6M8 10L12 6L16 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              {/* Confetti pieces */}
              <div className="absolute -top-8 -left-8 w-3 h-3 bg-[#fbbf24] rounded-full" />
              <div className="absolute -top-4 right-8 w-2 h-2 bg-[#22c55e] rounded-full" />
              <div className="absolute top-16 -right-12 w-4 h-4 bg-[#1453f5] rounded-full" />
              <div className="absolute bottom-0 -left-12 w-2 h-2 bg-[#ec4899] rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ============================================================================
// Dynamic DocuSign SDK Loader
// ============================================================================

let docuSignSdkPromise: Promise<void> | null = null;

const loadDocuSignSdk = (): Promise<void> => {
  if (window.DocuSign) return Promise.resolve();
  if (docuSignSdkPromise) return docuSignSdkPromise;
  docuSignSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = __DOCUSIGN_URL__;
    script.onload = () => resolve();
    script.onerror = () => {
      docuSignSdkPromise = null;
      reject(new Error("Failed to load DocuSign SDK"));
    };
    document.body.appendChild(script);
  });
  return docuSignSdkPromise;
};

// ============================================================================
// Required Fields Info Section Component
// ============================================================================

interface RequiredFieldsInfoSectionProps {
  notReadyCount: number;
  onCheckRequiredFields: () => void;
  validating?: boolean;
}

const RequiredFieldsInfoSection: React.FC<RequiredFieldsInfoSectionProps> = ({
  notReadyCount,
  onCheckRequiredFields,
  validating = false,
}) => (
  <div className="mt-6 rounded-lg border border-[#e5e7ec] bg-[#f3f5f8] p-4">
    <div className="flex gap-3 items-start">
      <AlertCircleIcon size={20} className="text-[#4a5466] mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-sm text-[#4a5466]">
          {notReadyCount > 0
            ? `Borrowers are not ready to sign because required fields are missing or incomplete.`
            : ""}
        </p>
        <Button
          onClick={onCheckRequiredFields}
          variant="primary"
          size="s"
          className="mt-3"
          disabled={validating}
        >
          {validating ? "Validating..." : "Check required fields"}
        </Button>
      </div>
    </div>
  </div>
);

// ============================================================================
// Main Signature Section Component
// ============================================================================

interface SignatureSectionProps {
  onCheckRequiredFields?: () => void;
  validating?: boolean;
  /** Flush pending autosave before each progress check (e.g. on Signatures entry). */
  onBeforeProgressCheck?: () => Promise<void>;
}

export const SignatureSection: React.FC<SignatureSectionProps> = ({
  onCheckRequiredFields,
  validating = false,
  onBeforeProgressCheck,
}) => {
  const store = useApplicationFormStore();
  const {
    data,
    currentStep,
    populateFromApi,
    updateBorrowerSignatureStatus,
    updateCoBorrowerSignatureStatus,
    isLockedForSigning,
  } = store;
  const api = useDgaApi();
  const navigate = useNavigate();
  const { dealer_id } = useDealer();
  const { closeDgaModal } = useDgaModal();

  // Local state
  const [filterTab, setFilterTab] = React.useState<FilterTab>("all");
  const [showEmailDialog, setShowEmailDialog] = React.useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(true);
  const [showDocusignModal, setShowDocusignModal] = React.useState(false);
  const [docusignLoading, setDocusignLoading] = React.useState(true);
  const [signingBorrowerId, setSigningBorrowerId] = React.useState<
    "primary" | "co" | null
  >(null);
  const previousBorrowerStatusRef = React.useRef<SignatureStatus>("ready");
  const previousCoBorrowerStatusRef = React.useRef<SignatureStatus>("ready");
  const [loadingProgress, setLoadingProgress] = React.useState(true);
  const [showEditDialog, setShowEditDialog] = React.useState(false);
  const [isVoiding, setIsVoiding] = React.useState(false);

  const isOnSignaturePage = currentStep === "signature";

  // "Awaiting Signature" blanket badge only applies when the backend milestone
  // is AWAITING_SIGNATURE (set when email signing is used). For "Sign Here"
  // (direct), each borrower manages their own status and the Sign dropdown
  // stays available so they can re-sign (invalidates old envelope).
  const isAwaitingSignature =
    data.signature.appMilestone ===
    DomainAppMilestoneType.AppMilestoneAwaitingSignature;

  const signingMode = getSigningMode(
    data.signature.envelopeId,
    data.signature.appMilestone,
  );

  // Reopen must wait while the backend processes a completed signing —
  // reopening mid-processing leaves the application stuck (DEA-946).
  const signingCompletionPending = isSigningCompletionPending(
    data.signature,
    data.hasCoBorrower,
  );

  // Hide tooltip when navigating away from the signature step.
  // The Transition component keeps this component mounted briefly during
  // exit animations, and the Radix Tooltip portal would remain visible
  // above the incoming page.
  React.useEffect(() => {
    if (!isOnSignaturePage) {
      setShowTooltip(false);
    }
  }, [isOnSignaturePage]);

  // Check if primary borrower info is filled
  const hasPrimaryBorrowerInfo =
    data.primaryBorrower.firstName && data.primaryBorrower.lastName;

  const hasCoBorrowerInfo =
    data.hasCoBorrower &&
    data.coBorrower?.firstName &&
    data.coBorrower?.lastName;

  const primaryBorrowerBase: BorrowerSignatureBase | null =
    hasPrimaryBorrowerInfo
      ? {
          id: "primary",
          name: `${data.primaryBorrower.firstName} ${data.primaryBorrower.lastName}`,
          role: "Primary Borrower",
          email: data.primaryBorrower.email || "",
          backendStatus: data.signature.borrowerStatus,
          initials:
            `${data.primaryBorrower.firstName[0]}${data.primaryBorrower.lastName[0]}`.toUpperCase(),
          avatarColor: "purple",
        }
      : null;

  const coBorrowerBase: BorrowerSignatureBase | null =
    hasCoBorrowerInfo && data.coBorrower
      ? {
          id: "co",
          name: `${data.coBorrower.firstName} ${data.coBorrower.lastName}`,
          role: "Co-Borrower",
          email: data.coBorrower.email,
          backendStatus: data.signature.coBorrowerStatus || "not-ready",
          initials:
            `${data.coBorrower.firstName[0]}${data.coBorrower.lastName[0]}`.toUpperCase(),
          avatarColor: "blue",
        }
      : null;

  const { borrowers, filteredBorrowers, counts, allSigned } =
    buildSignatureBorrowerView({
      primary: primaryBorrowerBase,
      co: coBorrowerBase,
      validation: {
        primaryPassed: store.frontendValidationPrimaryPassed,
        coPassed: store.frontendValidationCoPassed,
      },
      filterTab,
    });

  const emailRecipients = React.useMemo((): EmailRecipient[] => {
    const recipients: EmailRecipient[] = [];
    if (primaryBorrowerBase) {
      recipients.push({
        name: primaryBorrowerBase.name,
        role: primaryBorrowerBase.role,
        email: primaryBorrowerBase.email,
      });
    }
    if (coBorrowerBase) {
      recipients.push({
        name: coBorrowerBase.name,
        role: coBorrowerBase.role,
        email: coBorrowerBase.email,
      });
    }
    return recipients;
  }, [primaryBorrowerBase, coBorrowerBase]);

  const readyForLenders =
    allSigned &&
    data.signature.applicationStatus ===
      DomainApplicationStatus.ApplicationSubmitted;

  // Map backend borrower status to frontend SignatureStatus.
  const mapStatus = (
    backendStatus: string | undefined,
    currentFrontendStatus: SignatureStatus,
  ): SignatureStatus => {
    // Preserve "preparing" — user is actively creating an envelope,
    // backend status is stale (still READY_TO_SIGN_SUBMIT).
    if (currentFrontendStatus === "preparing") {
      return currentFrontendStatus;
    }

    switch (backendStatus) {
      case DomainBorrowerStatus.BorrowerReadyToSignSubmit:
        return "ready";
      case DomainBorrowerStatus.BorrowerSigned:
        return "signed";
      case DomainBorrowerStatus.BorrowerDeclinedSign:
        return "declined";
      case DomainBorrowerStatus.BorrowerWaitingOnESigSvc:
        return "validating";
      default:
        return "not-ready";
    }
  };

  const applySignatureMetadataFromProgress = (progress: {
    application_status?: string;
    application_milestone?: string;
    envelope_id?: string;
  }) => {
    const signaturePopulate: Partial<{
      applicationStatus: string;
      appMilestone: string;
      envelopeId: string | undefined;
    }> = {};
    if (
      progress.application_status &&
      progress.application_status !== signatureRef.current.applicationStatus
    ) {
      signaturePopulate.applicationStatus = progress.application_status;
    }
    if (
      progress.application_milestone &&
      progress.application_milestone !== signatureRef.current.appMilestone
    ) {
      signaturePopulate.appMilestone = progress.application_milestone;
    }
    const envelopeId = progress.envelope_id || undefined;
    if (envelopeId !== signatureRef.current.envelopeId) {
      signaturePopulate.envelopeId = envelopeId;
    }
    if (Object.keys(signaturePopulate).length > 0) {
      populateFromApi({ signature: signaturePopulate });
    }
  };

  const applyProgressValidation = (progress: {
    borrower?: DtoBorrowerProgress;
    co_borrowers?: DtoCoBorrowerProgress[];
  }) => {
    const backendErrors = {
      ...mapBorrowerProgressToFieldErrors(progress.borrower, "primaryBorrower"),
    };
    if (data.hasCoBorrower && progress.co_borrowers?.[0]) {
      Object.assign(
        backendErrors,
        mapBorrowerProgressToFieldErrors(
          progress.co_borrowers[0],
          "coBorrower",
        ),
      );
    }

    store.setBackendValidationErrors(backendErrors);

    const errors = mergeValidationErrors(
      validateApplication(data),
      backendErrors,
      { freshBackend: true },
    );

    const { primaryPassed, coPassed } = computeValidationFlags(errors);
    store.setValidationErrors(errors);
    store.setFrontendValidationPrimaryPassed(primaryPassed);
    store.setFrontendValidationCoPassed(coPassed);

    if (store.manualFieldValidationActive) {
      store.setAllFieldErrors({
        ...preserveApiFieldErrors(store.fieldErrors),
        ...errors,
      });
    }
  };

  // Fetch the latest backend state (progress + milestone) and apply it to the store.
  // Used after user-initiated actions so the UI updates immediately instead of
  // waiting for the next polling tick.
  const refreshFromBackend = async (appId: string) => {
    const resp = await api.calculateProgress(appId);
    const progress = resp.data as {
      application_status?: string;
      application_milestone?: string;
      envelope_id?: string;
      borrower?: DtoBorrowerProgress;
      co_borrowers?: DtoCoBorrowerProgress[];
    };

    applyProgressValidation(progress);

    applySignatureMetadataFromProgress(progress);

    if (progress.borrower) {
      const current = signatureRef.current.borrowerStatus;
      const newStatus = mapStatus(progress.borrower.status, current);
      if (newStatus !== current) {
        updateBorrowerSignatureStatus(newStatus);
      }
    }
    if (progress.co_borrowers?.length && data.hasCoBorrower) {
      const coProgress = progress.co_borrowers[0];
      const currentCo = signatureRef.current.coBorrowerStatus ?? "not-ready";
      const newCoStatus = mapStatus(coProgress.status, currentCo);
      if (newCoStatus !== currentCo) {
        updateCoBorrowerSignatureStatus(newCoStatus);
      }
    }
  };

  // Poll borrower status from backend every 5 seconds while on the Signatures page.
  // This detects signing completed via DocuSign (WAITING_ON_ESIGNATURE → SIGNED)
  // and also picks up signatures made via DLA in another browser.
  const signatureRef = React.useRef(data.signature);
  signatureRef.current = data.signature;

  const POLL_INTERVAL_MS = 5000;

  const onBeforeProgressCheckRef = React.useRef(onBeforeProgressCheck);
  onBeforeProgressCheckRef.current = onBeforeProgressCheck;

  React.useEffect(() => {
    if (!isOnSignaturePage) {
      return;
    }

    const appId = data.applicationId;
    if (!appId) {
      setLoadingProgress(false);
      return;
    }

    setLoadingProgress(true);

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchProgress = async () => {
      try {
        await onBeforeProgressCheckRef.current?.();
        if (cancelled) return;

        // Fetch progress (borrower statuses + application status + milestone)
        const resp = await api.calculateProgress(appId);
        if (cancelled) return;

        const progress = resp.data as {
          application_status?: string;
          application_milestone?: string;
          envelope_id?: string;
          borrower?: DtoBorrowerProgress;
          co_borrowers?: DtoCoBorrowerProgress[];
        };

        applyProgressValidation(progress);

        applySignatureMetadataFromProgress(progress);

        // Update primary borrower status
        if (progress.borrower) {
          const current = signatureRef.current.borrowerStatus;
          const newStatus = mapStatus(progress.borrower.status, current);
          if (newStatus !== current) {
            updateBorrowerSignatureStatus(newStatus);
          }
        }

        // Update co-borrower status
        if (progress.co_borrowers?.length && data.hasCoBorrower) {
          const coProgress = progress.co_borrowers[0];
          const currentCo =
            signatureRef.current.coBorrowerStatus ?? "not-ready";
          const newCoStatus = mapStatus(coProgress.status, currentCo);
          if (newCoStatus !== currentCo) {
            updateCoBorrowerSignatureStatus(newCoStatus);
          }
        }
      } catch (err) {
        console.error("[DGA] Failed to load progress for signatures:", err);
      } finally {
        if (!cancelled) setLoadingProgress(false);
      }
    };

    const startPolling = () => {
      if (cancelled) return;
      timeoutId = setTimeout(() => {
        void fetchProgress().then(() => startPolling());
      }, POLL_INTERVAL_MS);
    };

    // Initial fetch immediately, then start polling chain
    void fetchProgress().then(() => {
      startPolling();
    });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [data.applicationId, isOnSignaturePage]);

  // Show success dialog when all borrowers signed AND application is submitted
  React.useEffect(() => {
    if (readyForLenders) {
      setShowSuccessDialog(true);
    }
  }, [readyForLenders]);

  // Handlers
  const handleSignHere = async (borrowerId: "primary" | "co") => {
    // Guard: prevent opening a second Docusign session while one is active.
    if (showDocusignModal) return;

    const borrower = borrowers.find((b) => b.id === borrowerId);
    if (!borrower || borrower.status !== "ready") return;

    const borId =
      borrowerId === "primary" ? data.primaryBorrowerId : data.coBorrowerId;
    const appId = data.applicationId;

    if (!appId || !borId) return;

    previousBorrowerStatusRef.current = signatureRef.current.borrowerStatus;
    previousCoBorrowerStatusRef.current =
      signatureRef.current.coBorrowerStatus ?? "ready";

    const updateRelevantBorrowerStatus = (status: SignatureStatus) => {
      switch (borrowerId) {
        case "primary":
          updateBorrowerSignatureStatus(status);
          if (data.hasCoBorrower) {
            updateCoBorrowerSignatureStatus(
              previousCoBorrowerStatusRef.current,
            );
          }
          break;
        case "co":
          updateBorrowerSignatureStatus(previousBorrowerStatusRef.current);
          if (data.hasCoBorrower) {
            updateCoBorrowerSignatureStatus(status);
          }
          break;
      }
    };

    updateBorrowerSignatureStatus("preparing");
    if (data.hasCoBorrower) {
      updateCoBorrowerSignatureStatus("preparing");
    }

    try {
      // 1. Create envelope
      const envelopeResp = await api.createSigningEnvelope(appId);
      const envelopeData = envelopeResp.data as { envelope_id?: string };
      const envelopeId = envelopeData?.envelope_id;
      if (!envelopeId) throw new Error("No envelope_id returned");

      populateFromApi({ signature: { envelopeId } });

      // 2. Get signing URL
      const signResp = await api.signApplication(borId, {
        envelope_id: envelopeId,
        return_url: window.location.href,
      });
      const signData = signResp.data as { signing_url?: string };
      const signingUrl = signData?.signing_url;
      if (!signingUrl) throw new Error("No signing_url returned");

      // 3. Open modal and track which borrower is signing
      setSigningBorrowerId(borrowerId);
      setDocusignLoading(true);
      setShowDocusignModal(true);

      // Wait for #docusign-dga DOM element to be rendered by React
      await new Promise<void>((resolve, reject) => {
        let attempts = 0;
        const check = () => {
          if (document.getElementById("docusign-dga")) {
            resolve();
          } else if (attempts++ > 50) {
            reject(new Error("Docusign mount element not found"));
          } else {
            requestAnimationFrame(check);
          }
        };
        requestAnimationFrame(check);
      });

      // 4. Load Docusign SDK (no-op if already loaded from a prior signing)
      await loadDocuSignSdk();

      // 5. Initialize Docusign embedded signing
      const docusign = await window.DocuSign.loadDocuSign(
        __DOCUSIGN_INTEGRATION_KEY_ID__,
      );
      const signing = docusign.signing({
        url: signingUrl,
        displayFormat: "focused",
        style: {
          branding: {
            primaryButton: {
              color: "white",
              backgroundColor: "rgba(49, 57, 74, 1)",
            },
          },
          signingNavigationButton: {
            position: "bottom-right",
          },
        },
      });

      // 6. Handle Docusign events
      signing.on("ready", () => {
        setDocusignLoading(false);
      });

      signing.on("sessionEnd", (evt) => {
        // Clear signingBorrowerId BEFORE closing modal so onOpenChange
        // doesn't reset status back to previous statuses.
        setSigningBorrowerId(null);
        setShowDocusignModal(false);
        // Reset both borrowers out of "preparing" so mapStatus's guard
        // doesn't block refreshFromBackend from applying the new status.
        updateBorrowerSignatureStatus(previousBorrowerStatusRef.current);
        if (data.hasCoBorrower) {
          updateCoBorrowerSignatureStatus(previousCoBorrowerStatusRef.current);
        }
        // Only a completed signing records the e-signature. Recording it on
        // cancel/decline/"Finish Later" leaves the borrower validating with no
        // webhook coming, which blocks Reopen for Edit (DEA-946).
        if (!isDocusignSigningComplete(evt)) {
          void refreshFromBackend(appId).catch(() => {
            console.error("[DGA] Failed to fetch progress after Docusign exit");
          });
          return;
        }
        void (async () => {
          try {
            await api.markSigned(borId);
            // Immediately fetch backend state so the UI reflects the
            // signed status without waiting for the next polling tick.
            await refreshFromBackend(appId);
          } catch {
            console.error("[DGA] markSigned failed after Docusign session end");
            updateRelevantBorrowerStatus("ready");
          }
        })();
      });

      // 7. Mount Docusign into the modal's container element
      signing.mount("#docusign-dga");

      // 8. Trigger resize so Docusign recalculates its iframe dimensions
      window.dispatchEvent(new Event("resize"));
    } catch (err) {
      console.error("[DGA] Sign here failed:", err);
      updateRelevantBorrowerStatus("ready");
      toast.error("Failed to start signing. Please try again.");
      setSigningBorrowerId(null);
      setShowDocusignModal(false);
    }
  };

  const handleSignViaEmail = (borrowerId: "primary" | "co") => {
    const borrower = borrowers.find((b) => b.id === borrowerId);
    if (!borrower || borrower.status !== "ready") return;

    setShowEmailDialog(true);
  };

  const handleSendEmailInstructions = async () => {
    const appId = data.applicationId;

    if (!appId) return;

    setShowEmailDialog(false);
    // Set BOTH borrowers to "preparing" for email flow to prevent
    // sending again for the other borrower while the envelope is being created.
    updateBorrowerSignatureStatus("preparing");
    if (data.hasCoBorrower) {
      updateCoBorrowerSignatureStatus("preparing");
    }

    const resetBothBorrowersToReady = () => {
      updateBorrowerSignatureStatus("ready");
      if (data.hasCoBorrower) {
        updateCoBorrowerSignatureStatus("ready");
      }
    };

    try {
      await api.createSigningEnvelope(appId, true);
      // DocuSign sends signing email directly — no signApplication call needed.
      toast.success("Email sent");
      resetBothBorrowersToReady();
    } catch (err) {
      console.error(
        "%c[DGA] Email sign failed",
        "color: red; font-weight: bold",
        err,
      );
      toast.error("Email sign failed");
      resetBothBorrowersToReady();
      return;
    }

    // Immediately fetch backend state so the UI updates without waiting for polling.
    try {
      await refreshFromBackend(appId);
    } catch {
      console.error("[DGA] Failed to fetch progress after email send");
    }
  };

  const handleCloseAndNavigate = async () => {
    setShowSuccessDialog(false);
    await closeDgaModal();
    void navigate(`/${dealer_id}/applications/${data.applicationId}/lenders`);
  };

  // Handle edit application (void envelope and unlock for editing)
  const handleEditApplication = async () => {
    if (!data.applicationId) {
      toast.error("Application ID not found. Please try again.");
      return;
    }

    // Polling may have picked up a completed signing while the dialog was open
    if (signingCompletionPending) {
      setShowEditDialog(false);
      toast.error(
        "Signatures are still being processed. Please wait a moment and try again.",
      );
      return;
    }

    setIsVoiding(true);
    try {
      await api.reopenApplication(data.applicationId);

      // Immediately fetch the latest backend state so the UI reflects
      // the reopened application without waiting for the next polling tick.
      await refreshFromBackend(data.applicationId);

      // Close dialog and show success message
      setShowEditDialog(false);
      toast.success("Application unlocked for editing");
    } catch (error) {
      console.error("Failed to reopen application:", error);
      toast.error("Failed to void signature. Please try again.");
      // Keep dialog open on error - don't close it
    } finally {
      setIsVoiding(false);
    }
  };

  // Loading state while checking backend progress
  if (loadingProgress) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="flex flex-col gap-3 items-center">
          <div className="w-8 h-8 border-2 border-[#1453f5] border-t-transparent rounded-full animate-spin" />
          <p className="text-base text-[#6b7280]">
            Checking application status...
          </p>
        </div>
      </div>
    );
  }

  // Empty state when no borrower info
  if (borrowers.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center px-8 py-16 text-center">
        <div className="flex justify-center items-center mb-4 w-16 h-16 rounded-full bg-grey-200">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            className="text-[#9ca3af]"
          >
            <path
              d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx="9"
              cy="7"
              r="4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 21V19C21.9993 18.1137 21.7044 17.2528 21.1614 16.5523C20.6184 15.8519 19.8581 15.3516 19 15.13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="text-[18px] font-medium text-[#111827] mb-2">
          No borrower information
        </h3>
        <p className="text-base text-[#6b7280] max-w-sm">
          Please fill in the primary borrower details in the previous steps
          before proceeding with signatures.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex justify-between items-center">
        <div className="flex flex-1 justify-end">
          <FilterTabs
            activeTab={filterTab}
            onTabChange={setFilterTab}
            counts={counts}
          />
        </div>
      </div>

      {/* Signature cards */}
      <div className="space-y-3">
        {filteredBorrowers.map((borrower, index) => (
          <SignatureCard
            key={borrower.id}
            borrower={borrower}
            onSignHere={() => {
              void handleSignHere(borrower.id);
            }}
            onSignViaEmail={() => handleSignViaEmail(borrower.id)}
            showTooltip={
              isOnSignaturePage &&
              showTooltip &&
              index === 0 &&
              borrower.status === "ready"
            }
            onTooltipClose={() => setShowTooltip(false)}
            isAwaitingSignature={isAwaitingSignature}
            signingMode={signingMode}
          />
        ))}
      </div>

      {/* Required fields info section */}
      {counts.notReady > 0 && onCheckRequiredFields && (
        <RequiredFieldsInfoSection
          notReadyCount={counts.notReady}
          onCheckRequiredFields={onCheckRequiredFields}
          validating={validating}
        />
      )}
      {/* Re-open for edit section - only shows when application is locked for signing */}
      {isLockedForSigning && (
        <div className="mt-6 rounded-lg border border-[#eab308] bg-[#fefce8] p-4">
          <div className="flex gap-3 items-start">
            <AlertCircleIcon
              size={20}
              className="text-[#eab308] mt-0.5 shrink-0"
            />
            <div className="flex-1">
              <p className="text-sm text-[#4a5466]">
                {signingCompletionPending
                  ? "Signatures are being processed. You'll be able to re-open the application for editing once processing finishes."
                  : "At least one borrower has begun signing the application, so it is locked for further edits. To make changes, you'll need to invalidate the signature and re-open the application for editing."}
              </p>
              <Button
                onClick={() => setShowEditDialog(true)}
                variant="secondary"
                size="s"
                className="mt-3"
                disabled={signingCompletionPending}
              >
                Re-open for editing
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email dialog */}
      <SignViaEmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        recipients={emailRecipients}
        onSend={() => {
          void handleSendEmailInstructions();
        }}
      />

      {/* Success dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        onProceedToLenders={() => void handleCloseAndNavigate()}
      />

      {/* Docusign signing modal */}
      <Dialog
        open={showDocusignModal}
        onOpenChange={(open) => {
          if (!open && signingBorrowerId) {
            updateBorrowerSignatureStatus(previousBorrowerStatusRef.current);
            if (data.hasCoBorrower) {
              updateCoBorrowerSignatureStatus(
                previousCoBorrowerStatusRef.current,
              );
            }
            setSigningBorrowerId(null);
            setShowDocusignModal(false);
          }
        }}
      >
        <DialogContent className="max-w-4xl h-[calc(100vh-4rem)] p-0 flex flex-col">
          <DialogHeader className="p-4 border-b shrink-0">
            <DialogTitle>Sign Application</DialogTitle>
          </DialogHeader>
          <div className="relative flex-1 min-h-0">
            {docusignLoading && (
              <div className="flex absolute inset-0 z-10 justify-center items-center bg-white">
                <div className="flex flex-col gap-3 items-center">
                  <div className="w-8 h-8 border-2 border-[#1453f5] border-t-transparent rounded-full animate-spin" />
                  <p className="text-base text-[#6b7280]">
                    Preparing documents for signing...
                  </p>
                </div>
              </div>
            )}
            <div
              id="docusign-dga"
              className="w-full h-full [&_iframe]:min-h-full!"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit application dialog */}
      <EditApplicationDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onConfirm={() => void handleEditApplication()}
        isVoiding={isVoiding}
      />
    </div>
  );
};

export default SignatureSection;
